<template>
  <div class="lobby w-[100lvw] h-[100lvh] bg-cw-orange flex flex-col">
    <NestBanner />
    <div class="fixed top-[30px] right-[30px]" v-if="!isKiddiesAccount">
      <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="handleSoundToggle">
        <SpeakerMuteIcon v-if="!isSoundEnabled" class="w-5 h-5 md:w-6 md:h-6" />
        <SpeakerIcon v-else class="w-5 h-5 md:w-6 md:h-6" />
      </BaseButton>
    </div>

    <div class="fixed top-[30px] left-[30px]" v-if="!isKiddiesAccount">
      <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="goToSettings">
        <SettingsIcon />
      </BaseButton>
    </div>

    <div class="flex-1 flex gap-x-14 md:gap-x-20 lg:gap-x-24 xl:gap-x-28 justify-center items-center">
      <div class="bg-[#FFBA66] rounded-[18px] w-max text-center">
        <h1
          class="text-6xl md:text-7xl lg:text-9xl xl:text-[160px] leading-14 md:leading-16 lg:leading-[115px] xl:leading-[140px] font-bold *:text-[#FFE8CC] *:-tracking-[5px] *:md:-tracking-[7px] *:lg:-tracking-[10px] *:xl:-tracking-[14px]"
        >
          <span class="block rotate-[5.492deg]">Plan</span>
          <span class="block -rotate-[1.864deg]">Save</span>
          <span class="block rotate-[4.562deg]">Invest</span>
        </h1>
      </div>
      <div class="grid grid-cols-1 gap-y-4 lg:gap-y-7 *:!w-full">
        <BaseButton background-color="#1AA570" border-color="var(--color-cw-green-border-shadow)" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" @click="openNewGameModal">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Start a game</p>
        </BaseButton>
        <BaseButton background-color="var(--color-cw-purple)" border-color="#1B0623" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" @click="openJoinGameModal">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">Join a game</p>
        </BaseButton>
        <BaseButton background-color="var(--color-cw-indigo)" border-color="#1F2547" class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5" @click="openPlayComputerModal">
          <p class="w-full !text-sm md:!text-base xl:!text-xl">You vs Computer</p>
        </BaseButton>
      </div>
    </div>
    <div class="px-[30px] pb-[30px] flex justify-between items-end">
      <BaseButton background-color="var(--color-cw-red-light)" border-color="#471F20" @click="exitGame">
        <ArrowFatLeftIcon />
        <p class="text-sm lg:text-base font-bold">{{ isKiddiesAccount ? 'Exit game' : 'Logout' }}</p>
      </BaseButton>
      <p class="mb-1.5 text-sm lg:text-base">A game by YellowCowries</p>
      <div class="self-center flex gap-x-4 lg:gap-x-6 items-center">
        <BaseButton background-color="var(--color-cw-olive)" v-if="!isKiddiesAccount" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="goToLeaderboard">
          <ChartBarIcon />
        </BaseButton>
        <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="goToGuide">
          <BookIcon />
          <span id="book-icon" class="absolute left-[9px] top-2 bottom-px heightMd:bottom-[3px] rounded-xl right-[9px] heightMd:right-[9px]"></span>
        </BaseButton>
        <BaseButton background-color="var(--color-cw-olive)" v-if="!isKiddiesAccount" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="cardShopModalRef?.open">
          <CartIcon />
        </BaseButton>
      </div>
    </div>
    <NewGameModal ref="newGameModalRef" />
    <JoinGameModal ref="joinGameModalRef" />
    <PlayComputerModal ref="playComputerModalRef" />
    <OpenTutorialModal ref="openTutorialModalRef" />
    <HowToPlayModal ref="howToPlayModalRef" />
    <CardShopModal ref="cardShopModalRef" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, useTemplateRef, nextTick, watch } from 'vue'
import BaseButton from '@/components/base/BaseButton.vue'
import NewGameModal from '@/components/modals/lobby/NewGameModal.vue'
import NestBanner from '@/components/interface/NestBanner.vue'
import JoinGameModal from '@/components/modals/lobby/JoinGameModal.vue'
import PlayComputerModal from '@/components/modals/lobby/PlayComputerModal.vue'
import HowToPlayModal from '@/components/modals/lobby/HowToPlayModal.vue'
import CardShopModal from '@/components/modals/lobby/CardShopModal.vue'
import OpenTutorialModal from '@/components/modals/lobby/OpenTutorialModal.vue'
import ArrowFatLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'
import ChartBarIcon from '@/assets/svgs/icons/chart-bar.svg'
import SettingsIcon from '@/assets/svgs/icons/settings.svg'
import BookIcon from '@/assets/svgs/icons/book.svg'
import CartIcon from '@/assets/svgs/icons/cart.svg'
import SpeakerIcon from '@/assets/svgs/icons/speaker-simple-high-fill.svg'
import SpeakerMuteIcon from '@/assets/svgs/icons/speaker-simple-x-fill.svg'
import { storeToRefs } from 'pinia'
import { useAppStore } from '@/stores/app'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useHelpers } from '@/composables/useHelpers'
import { AppWindowEvent } from '@/types/Events'
import { driver, type Driver, type PopoverDOM } from 'driver.js'
import 'driver.js/dist/driver.css'
import useSocket from '@/composables/useSocket'
import { useOrientation } from '@/composables/useOrientation'
import { readStorage, removeStorage, writeStorage } from '@/composables/useStorage'

const newGameModalRef = useTemplateRef('newGameModalRef')
const joinGameModalRef = useTemplateRef('joinGameModalRef')
const playComputerModalRef = useTemplateRef('playComputerModalRef')
const openTutorialModalRef = useTemplateRef('openTutorialModalRef')
const howToPlayModalRef = useTemplateRef('howToPlayModalRef')
const cardShopModalRef = useTemplateRef('cardShopModalRef')
const { showLearning, showSettings, showLeaderboard, isSoundEnabled } = storeToRefs(useAppStore())
const { isKiddiesAccount, user } = storeToRefs(useUserStore())
const { toggleSound } = useAppStore()
const router = useRouter()
const { sendParentWindowMessage, removeCookie, sleep } = useHelpers()
const socket = useSocket()
const { isLandscape } = useOrientation()

const hasSeenHighlight = readStorage('local', 'hasCompletedTour') === 'true'

const handleSoundToggle = () => {
  toggleSound()
}

const openNewGameModal = () => {
  newGameModalRef.value?.open()
}

const openJoinGameModal = () => {
  joinGameModalRef.value?.open()
}

const openPlayComputerModal = () => {
  playComputerModalRef.value?.open()
}

const goToGuide = () => {
  if (isKiddiesAccount.value) {
    showLearning.value = true
  } else {
    openTutorialModalRef.value?.open()
  }
}

const goToSettings = () => {
  showSettings.value = true
}

const goToLeaderboard = () => {
  showLeaderboard.value = true
}

const logout = () => {
  socket.disconnect()
  router.push('/logout')
}

let driverObj: Driver | null
let orientationTimeout: ReturnType<typeof setTimeout> | null = null

const forceCleanupOverlays = (retries = 3) => {
  const cleanup = () => {
    // Remove all possible driver.js overlay elements
    const overlays = document.querySelectorAll('.driver-overlay, .driver-popover, .driver-active-element, .driver-highlighted-element')
    overlays.forEach(element => element.remove())

    // Remove driver.js body classes that block pointer events
    document.body.classList.remove('driver-active', 'driver-fade', 'driver-simple')
    document.body.style.overflow = ''
    document.documentElement.style.overflow = ''

    // Ensure any z-index blocking elements are cleared
    const highlightedElements = document.querySelectorAll('[style*="z-index"]')
    highlightedElements.forEach(element => {
      const htmlElement = element as HTMLElement
      if (htmlElement.style.zIndex && parseInt(htmlElement.style.zIndex) > 1000) {
        htmlElement.style.zIndex = ''
      }
    })

    // If elements still exist and we have retries left, try again
    const remainingOverlays = document.querySelectorAll('.driver-overlay, .driver-popover')
    if (remainingOverlays.length > 0 && retries > 0) {
      setTimeout(() => forceCleanupOverlays(retries - 1), 50)
    }
  }

  // Run cleanup immediately, then with a small delay if needed
  cleanup()
  if (retries > 0) {
    setTimeout(cleanup, 10)
  }
}

if (!hasSeenHighlight) {
  driverObj = driver({
    popoverOffset: 32,
    showButtons: ['close', 'next'],
    doneBtnText: ' ',
    disableButtons: ['next'],
    stageRadius: 6,
    onPopoverRender: (popover: PopoverDOM) => {
      const footer = popover.footer
      const closeBtn = popover.closeButton

      closeBtn.style.color = '#ffffff'
      closeBtn.style.marginTop = '2px'

      if (footer) {
        const letsGoBtn = document.createElement('button')
        if (letsGoBtn) {
          letsGoBtn.textContent = "Let's go"
          letsGoBtn.className =
            '!bg-cw-green !text-white  !mr-4 !px-4 !py-2 !rounded-[8px] !border-0 !text-shadow-none !font-semibold hover:!bg-cw-green-alt focus:!bg-cw-green !transition !duration-150 !ease-in-out !text-sm'

          letsGoBtn.addEventListener('click', () => {
            driverObj?.destroy()
            showLearning.value = true
          })

          footer.insertBefore(letsGoBtn, footer.firstChild)
        }
      }
    },

    steps: [
      {
        element: '#book-icon',

        popover: {
          side: 'top',
          align: 'end',
          popoverClass: 'driverjs-intro',

          title: `👋 Welcome, ${user.value?.firstName || user.value?.username}`,
          description: "Let's show you how to play. You'll be a star player in no time."
        }
      }
    ],
    onCloseClick: () => {
      writeStorage('local', 'hasCompletedTour', 'true')
      driverObj?.destroy()
    },
    onDestroyed: () => {
      writeStorage('local', 'hasCompletedTour', 'true')
      forceCleanupOverlays()
    }
  })
}

watch(isLandscape, (newValue, oldValue) => {
  // Clear any pending orientation timeout
  if (orientationTimeout) {
    clearTimeout(orientationTimeout)
    orientationTimeout = null
  }

  if (!newValue && oldValue && driverObj?.isActive()) {
    driverObj.destroy()
    forceCleanupOverlays()
  } else if (newValue && !oldValue && !hasSeenHighlight && driverObj) {
    orientationTimeout = setTimeout(() => {
      if (isLandscape.value && !driverObj?.isActive()) {
        driverObj?.drive()
      }
    }, 200)
  }
})

onMounted(async () => {
  await sleep(800)
  await nextTick()

  if (isLandscape.value && driverObj && !hasSeenHighlight) {
    driverObj.drive()
  }
})

onUnmounted(() => {
  // Cleanup on component unmount
  if (orientationTimeout) {
    clearTimeout(orientationTimeout)
  }
  if (driverObj?.isActive()) {
    driverObj.destroy()
  }
  forceCleanupOverlays()
})

const exitGame = () => {
  if (isKiddiesAccount.value) {
    removeStorage('session', 'user')
    removeCookie('access_token')
    sendParentWindowMessage(AppWindowEvent.ExitGame)
  } else {
    logout()
  }
}
</script>

<style>
.lobby {
  background-image: url('@/assets/images/spiral.svg'), url('@/assets/images/noise-texture.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.driver-popover.driverjs-intro {
  background-color: var(--color-cw-dark-alt);
  padding-left: 20px;
  padding-right: 15px;
  padding-top: 25px;
  padding-bottom: 20px;
  border-radius: 12px;
  max-width: 380px;
}

.driverjs-intro .driver-popover-footer .driver-popover-next-btn {
  margin-left: 0 !important;
  margin-right: auto !important;
}

.driver-popover.driverjs-intro .driver-popover-title {
  font-size: 20px;
  color: #fff;
  font-family: Rency;
  font: bold;
  letter-spacing: normal;
}

.driver-popover.driverjs-intro .driver-popover-description {
  color: var(--color-cw-grey-2);
  font-size: 14px;
  font-family: Rency;
  letter-spacing: normal;
  font-weight: 500;
  margin-bottom: 30px;
}

.driver-popover.driverjs-intro button {
  font-family: Rency;
  font-size: 12px;
  font-style: normal;
  padding: 5px;
  letter-spacing: normal;
  margin-top: -20px;
}

.driver-popover.driverjs-intro .driver-popover-arrow-side-left.driver-popover-arrow {
  border-left-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-intro .driver-popover-arrow-side-right.driver-popover-arrow {
  border-right-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-intro .driver-popover-arrow-side-top.driver-popover-arrow {
  border-top-color: var(--color-cw-dark-alt);
}

.driver-popover.driverjs-intro .driver-popover-arrow-side-bottom.driver-popover-arrow {
  border-bottom-color: var(--color-cw-dark-alt);
}

.driver-popover-footer .driverjs-intro {
  margin-top: 20px;
}

.driver-popover-close-btn {
  font-size: 20px !important;
}
</style>
